/**
 * The welcome offer, in one place.
 *
 * Three values, and the owner changes them here and nowhere else: how much off,
 * the code a merchant types, and the day it stops. Everything on the page that
 * mentions the offer, the scratch card, the pill that reopens it, the reveal and
 * its end date, reads these, so an expired offer cannot survive anywhere on the
 * page by being hard-coded into a sentence.
 *
 * After `endsAt` the card never opens and the pill never renders. There is no
 * per-visitor countdown that restarts for the next person: the date below is the
 * real one, the same for everybody, and any time-left shown counts to it.
 *
 * The code is real money now: the billing page sends whatever is parked here
 * with the first plan checkout, and the API asks Whop whether a promo code by
 * that name is live. Whop's checkout cannot be opened with a code already in
 * it, so a live code is shown to the merchant to type on Whop's payment page;
 * one that is not live opens the checkout at full price and says so. What this
 * file cannot do is create the code: `scripts/whop-create-plans.mjs` creates
 * it on Whop (sandbox for staging, the live account for production), ending
 * on `endsAt` below.
 */

export type Offer = {
  /** Percent off, as a whole number. Shown as-is in the copy. */
  percent: number;
  /** What the merchant types, and what travels on the sign-up link. */
  code: string;
  /** The last moment the offer is live, as an ISO instant with its zone. */
  endsAt: string;
};

export const OFFER: Offer = {
  percent: 20,
  code: 'WELCOME20',
  endsAt: '2026-10-21T23:59:59+03:00',
};

/** The query the sign-up link carries, and the key it is parked under. */
export const PROMO_PARAM = 'promo';
export const PROMO_STORAGE_KEY = 'krevya.promo';

export function offerIsLive(now: Date = new Date()): boolean {
  return now.getTime() < Date.parse(OFFER.endsAt);
}

/**
 * The end date, written the way the reader's own region writes a date.
 *
 * It takes a full tag ("tr-TR", "en-DE") and uses it. It used to map the two
 * languages onto two tags itself, which quietly ignored the region — and read
 * `locale === 'tr'` against a string that is now "tr-TR", so every Turkish
 * visitor would have been given the British date.
 */
export function offerEndsOn(formatLocale: string): string {
  return new Date(OFFER.endsAt).toLocaleDateString(formatLocale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** Whole days left, floored, never negative. */
export function offerDaysLeft(now: Date = new Date()): number {
  const ms = Date.parse(OFFER.endsAt) - now.getTime();
  return Math.max(0, Math.floor(ms / 86_400_000));
}

/**
 * Where "Use it" goes.
 *
 * The code rides on the URL and is also parked in session storage, because
 * signing up is a round trip (the form, possibly an e-mail, then the app) and a
 * query parameter does not survive it. Session rather than local: an offer a
 * visitor abandoned three weeks ago should not resurface inside their account.
 */
export function claimHref(): string {
  return `/register?${PROMO_PARAM}=${encodeURIComponent(OFFER.code)}`;
}

export function rememberPromo(): void {
  try {
    window.sessionStorage.setItem(PROMO_STORAGE_KEY, OFFER.code);
  } catch {
    // Private mode, blocked storage, a full quota. The URL still carries it.
  }
}

/**
 * Parks the code off `?promo=` for a visitor who arrived by a shared link.
 *
 * Only this offer's own code is honoured. The parameter is a public part of a
 * URL anybody can edit, and a stranger's invented string is not something to
 * carry into a checkout and show as a discount that is coming.
 */
export function rememberPromoFromUrl(): void {
  const sent = new URLSearchParams(window.location.search).get(PROMO_PARAM);
  if (sent && sent.toUpperCase() === OFFER.code && offerIsLive()) rememberPromo();
}

/**
 * The code waiting to be spent, if there is one and it is still worth sending.
 *
 * Past `endsAt` this answers null whatever is in storage, so a session left
 * open across the end of the offer stops asking for a discount that no longer
 * exists rather than being told no by Whop.
 */
export function parkedPromo(): string | null {
  if (!offerIsLive()) return null;
  try {
    const parked = window.sessionStorage.getItem(PROMO_STORAGE_KEY);
    return parked === OFFER.code ? parked : null;
  } catch {
    return null;
  }
}

/** Spent, or no longer wanted. Called once a checkout has actually been paid. */
export function forgetPromo(): void {
  try {
    window.sessionStorage.removeItem(PROMO_STORAGE_KEY);
  } catch {
    // Nothing to forget, and nothing anybody needs to be told about.
  }
}
